import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../../lib/cx.js';

export interface MenuProps {
  /** Rendered inside the trigger button. */
  trigger: ReactNode;
  label: string;
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  className?: string;
  triggerClassName?: string;
}

/**
 * Dropdown menu with roving focus: Arrow keys move, Escape closes and restores
 * focus, an outside click dismisses. Items are plain `MenuItem` buttons.
 */
export function Menu({
  trigger,
  label,
  children,
  align = 'end',
  className,
  triggerClassName,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = Array.from(
        rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      );
      if (items.length === 0) return;
      event.preventDefault();
      const current = items.indexOf(document.activeElement as HTMLElement);
      const next =
        event.key === 'ArrowDown'
          ? (current + 1 + items.length) % items.length
          : (current - 1 + items.length) % items.length;
      items[next]?.focus();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cx('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          'inline-flex items-center gap-1 rounded-[var(--radius-sm)] text-[var(--text-base)]',
          'transition-colors duration-[var(--dur-fast)]',
          triggerClassName ??
            'h-8 border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 hover:bg-[var(--surface-2)]',
        )}
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          aria-label={label}
          className={cx(
            'vam-enter absolute z-40 mt-1 min-w-[200px] overflow-hidden rounded-[var(--radius-md)]',
            'border border-[var(--border)] bg-[var(--surface)] py-1 shadow-[var(--shadow-2)]',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {children(() => {
            setOpen(false);
            triggerRef.current?.focus();
          })}
        </div>
      )}
    </div>
  );
}

export interface MenuItemProps {
  onClick?: () => void;
  children: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  destructive?: boolean;
  /** Shown right-aligned: a keyboard hint or a checkmark. */
  trailing?: ReactNode;
}

export function MenuItem({
  onClick,
  children,
  icon,
  disabled,
  destructive,
  trailing,
}: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[var(--text-base)]',
        'transition-colors duration-[var(--dur-fast)] disabled:cursor-not-allowed disabled:opacity-50',
        destructive
          ? 'text-[var(--danger)] hover:bg-[var(--danger-soft)]'
          : 'text-[var(--text)] hover:bg-[var(--surface-2)]',
      )}
    >
      {icon && <span className="text-[var(--text-3)]">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing && <span className="text-[var(--text-3)] tnum">{trailing}</span>}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-[var(--border)]" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-3 pb-1 pt-2 text-[var(--text-xs)] font-semibold uppercase tracking-wide text-[var(--text-3)]">
      {children}
    </p>
  );
}
